/**
 * Change Template Form Page
 * Create and edit reusable change templates
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Save2,
  DocumentText,
  Add,
  Trash,
  Edit2,
} from 'iconsax-react';
import {
  getTemplate,
  createTemplate,
  updateTemplate,
  getTeams,
  ChangeTemplate,
  TemplateCustomField,
  Team,
  ChangeCategory,
  RiskLevel,
  getChangeCategories,
  ChangeCategoryDef,
} from '../../services/changeManagementApi';
import toast from 'react-hot-toast';
import { useRBAC } from '../../contexts/RBACContext';

const TemplateFormPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { canAccess } = useRBAC();
  const isEdit = !!id;

  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(isEdit);
  const [teams, setTeams] = useState<Team[]>([]);
  const [customFields, setCustomFields] = useState<TemplateCustomField[]>([]);
  const [showFieldForm, setShowFieldForm] = useState(false);
  const [editingFieldIndex, setEditingFieldIndex] = useState<number | null>(null);
  const [categories, setCategories] = useState<ChangeCategoryDef[]>([]);

  const [formData, setFormData] = useState<Partial<ChangeTemplate>>({
    template_name: '',
    description: '',
    category: 'infrastructure',
    sub_category: '',
    team: '',
    default_title_format: '',
    implementation_plan: '',
    rollback_plan: '',
    test_plan: '',
    risk_level: 'low',
    estimated_duration: undefined,
    requires_downtime: false,
    auto_approve: false,
    is_active: true,
  });

  const categoryOptions: ChangeCategory[] =
    categories.length > 0
      ? (categories.map((c) => c.code) as ChangeCategory[])
      : (['infrastructure', 'rule', 'integration', 'security', 'network', 'application', 'configuration', 'other'] as ChangeCategory[]);

  const riskOptions: RiskLevel[] = ['low', 'medium', 'high'];

  useEffect(() => {
    const loadData = async () => {
      try {
        const [teamsData, categoriesData] = await Promise.all([
          getTeams(),
          getChangeCategories(true),
        ]);
        setTeams(teamsData);
        setCategories(categoriesData);

        if (isEdit && id) {
          const template = await getTemplate(id);
          setFormData({
            template_name: template.template_name || '',
            description: template.description || '',
            category: template.category || 'infrastructure',
            sub_category: template.sub_category || '',
            team: template.team || '',
            default_title_format: template.default_title_format || '',
            implementation_plan: template.implementation_plan || '',
            rollback_plan: template.rollback_plan || '',
            test_plan: template.test_plan || '',
            risk_level: template.risk_level || 'low',
            estimated_duration: template.estimated_duration,
            requires_downtime: template.requires_downtime || false,
            auto_approve: template.auto_approve || false,
            is_active: template.is_active !== undefined ? template.is_active : true,
          });
          setCustomFields(template.custom_fields || []);
        }
      } catch (error: any) {
        console.error('Error loading data:', error);
        toast.error(error.response?.data?.error || 'Failed to load template');
        navigate('/change-templates');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [id, isEdit, navigate]);

  const handleSubmit = async () => {
    // Validation
    if (!formData.template_name?.trim()) {
      toast.error('Template name is required');
      return;
    }
    if (!formData.team) {
      toast.error('Team is required');
      return;
    }
    if (!formData.category) {
      toast.error('Category is required');
      return;
    }

    setSaving(true);
    try {
      const submitData = {
        ...formData,
        custom_fields: customFields,
      };
      if (isEdit && id) {
        await updateTemplate(id, submitData);
        toast.success('Template updated successfully');
      } else {
        await createTemplate(submitData);
        toast.success('Template created successfully');
      }
      navigate('/change-templates');
    } catch (error: any) {
      console.error('Error saving template:', error);
      toast.error(error.response?.data?.error || 'Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const handleAddField = () => {
    setEditingFieldIndex(null);
    setShowFieldForm(true);
  };

  const handleEditField = (index: number) => {
    setEditingFieldIndex(index);
    setShowFieldForm(true);
  };

  const handleDeleteField = (index: number) => {
    setCustomFields(prev => prev.filter((_, i) => i !== index));
    toast.success('Custom field removed');
  };

  const handleSaveField = (field: TemplateCustomField) => {
    if (editingFieldIndex !== null) {
      // Update existing field
      setCustomFields(prev => {
        const updated = [...prev];
        updated[editingFieldIndex] = field;
        return updated;
      });
      toast.success('Custom field updated');
    } else {
      // Add new field
      setCustomFields(prev => [...prev, field]);
      toast.success('Custom field added');
    }
    setShowFieldForm(false);
    setEditingFieldIndex(null);
  };

  const handleCancelField = () => {
    setShowFieldForm(false);
    setEditingFieldIndex(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/change-templates')}
            className="p-2 rounded-lg bg-background-card border border-border-dark hover:bg-background-hover transition-colors"
          >
            <ArrowLeft size={20} color="#9CA3AF" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-white">
              {isEdit ? 'Edit Template' : 'New Template'}
            </h1>
            <p className="text-gray-400 mt-1">
              {isEdit ? 'Update template details' : 'Create a reusable change request template'}
            </p>
          </div>
        </div>
        <button
          onClick={handleSubmit}
          disabled={saving}
          className="flex items-center gap-2 px-4 py-2 bg-primary rounded-lg text-white hover:bg-primary/80 transition-colors disabled:opacity-50"
        >
          <Save2 size={18} />
          <span>{saving ? 'Saving...' : 'Save Template'}</span>
        </button>
      </div>

      {/* Form */}
      <div className="bg-background-card rounded-xl p-6 border border-border-dark space-y-6">
        {/* Basic Information */}
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            <DocumentText size={20} color="#426CFF" />
            Basic Information
          </h2>

          <div>
            <label className="block text-sm text-gray-400 mb-1">
              Template Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.template_name || ''}
              onChange={e => setFormData({ ...formData, template_name: e.target.value })}
              placeholder="e.g., Standard Firewall Rule Addition"
              className="w-full px-4 py-2 bg-background-hover rounded-lg border border-border-dark text-white placeholder-gray-500 focus:outline-none focus:border-primary"
            />
          </div>

          <div>
            <label className="block text-sm text-gray-400 mb-1">Description</label>
            <textarea
              value={formData.description || ''}
              onChange={e => setFormData({ ...formData, description: e.target.value })}
              placeholder="Describe when and how to use this template..."
              rows={3}
              className="w-full px-4 py-2 bg-background-hover rounded-lg border border-border-dark text-white placeholder-gray-500 focus:outline-none focus:border-primary resize-none"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-400 mb-1">
                Team <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.team || ''}
                onChange={e => {
                  const newValue = e.target.value;
                  setFormData(prev => ({ ...prev, team: newValue }));
                }}
                className="w-full px-4 py-2 bg-background-hover rounded-lg border border-border-dark text-white focus:outline-none focus:border-primary"
              >
                <option value="">Select Team</option>
                {teams.map(team => (
                  <option key={team.id} value={team.id}>
                    {team.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm text-gray-400 mb-1">
                Category <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.category || 'infrastructure'}
                onChange={e => {
                  const newValue = e.target.value as ChangeCategory;
                  setFormData(prev => ({ ...prev, category: newValue }));
                }}
                className="w-full px-4 py-2 bg-background-hover rounded-lg border border-border-dark text-white focus:outline-none focus:border-primary"
              >
                {categoryOptions.map(cat => {
                  const def = categories.find(c => c.code === cat);
                  const label = def?.name || cat;
                  return (
                    <option key={cat} value={cat}>
                      {label.charAt(0).toUpperCase() + label.slice(1)}
                    </option>
                  );
                })}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm text-gray-400 mb-1">Sub Category</label>
            <input
              type="text"
              value={formData.sub_category || ''}
              onChange={e => setFormData({ ...formData, sub_category: e.target.value })}
              placeholder="Optional sub-categorization"
              className="w-full px-4 py-2 bg-background-hover rounded-lg border border-border-dark text-white placeholder-gray-500 focus:outline-none focus:border-primary"
            />
          </div>
        </div>

        {/* Template Content */}
        <div className="space-y-4 pt-4 border-t border-border-dark">
          <h2 className="text-lg font-semibold text-white">Template Content</h2>

          <div>
            <label className="block text-sm text-gray-400 mb-1">Default Title Format</label>
            <input
              type="text"
              value={formData.default_title_format || ''}
              onChange={e => setFormData({ ...formData, default_title_format: e.target.value })}
              placeholder="e.g., Add firewall rule for {service}"
              className="w-full px-4 py-2 bg-background-hover rounded-lg border border-border-dark text-white placeholder-gray-500 focus:outline-none focus:border-primary"
            />
            <p className="text-xs text-gray-500 mt-1">
              Use {'{variable}'} placeholders that can be filled when creating a change from this template
            </p>
          </div>

          <div>
            <label className="block text-sm text-gray-400 mb-1">Implementation Plan</label>
            <textarea
              value={formData.implementation_plan || ''}
              onChange={e => setFormData({ ...formData, implementation_plan: e.target.value })}
              placeholder="Step-by-step implementation instructions..."
              rows={6}
              className="w-full px-4 py-2 bg-background-hover rounded-lg border border-border-dark text-white placeholder-gray-500 focus:outline-none focus:border-primary resize-none"
            />
          </div>

          <div>
            <label className="block text-sm text-gray-400 mb-1">Rollback Plan</label>
            <textarea
              value={formData.rollback_plan || ''}
              onChange={e => setFormData({ ...formData, rollback_plan: e.target.value })}
              placeholder="Steps to rollback if implementation fails..."
              rows={4}
              className="w-full px-4 py-2 bg-background-hover rounded-lg border border-border-dark text-white placeholder-gray-500 focus:outline-none focus:border-primary resize-none"
            />
          </div>

          <div>
            <label className="block text-sm text-gray-400 mb-1">Test Plan</label>
            <textarea
              value={formData.test_plan || ''}
              onChange={e => setFormData({ ...formData, test_plan: e.target.value })}
              placeholder="Testing procedures and validation steps..."
              rows={4}
              className="w-full px-4 py-2 bg-background-hover rounded-lg border border-border-dark text-white placeholder-gray-500 focus:outline-none focus:border-primary resize-none"
            />
          </div>
        </div>

        {/* Risk & Settings */}
        <div className="space-y-4 pt-4 border-t border-border-dark">
          <h2 className="text-lg font-semibold text-white">Risk Assessment & Settings</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-400 mb-1">Risk Level</label>
              <select
                value={formData.risk_level || 'low'}
                onChange={e => {
                  const newValue = e.target.value as RiskLevel;
                  setFormData(prev => ({ ...prev, risk_level: newValue }));
                }}
                className="w-full px-4 py-2 bg-background-hover rounded-lg border border-border-dark text-white focus:outline-none focus:border-primary"
              >
                {riskOptions.map(risk => (
                  <option key={risk} value={risk}>
                    {risk.charAt(0).toUpperCase() + risk.slice(1)}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm text-gray-400 mb-1">Estimated Duration (minutes)</label>
              <input
                type="number"
                value={formData.estimated_duration || ''}
                onChange={e => setFormData({ ...formData, estimated_duration: e.target.value ? parseInt(e.target.value) : undefined })}
                placeholder="e.g., 60"
                min="0"
                className="w-full px-4 py-2 bg-background-hover rounded-lg border border-border-dark text-white placeholder-gray-500 focus:outline-none focus:border-primary"
              />
            </div>
          </div>

          <div className="space-y-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.requires_downtime || false}
                onChange={e => setFormData({ ...formData, requires_downtime: e.target.checked })}
                className="w-4 h-4 rounded border-border-dark bg-background-hover text-primary focus:ring-primary"
              />
              <span className="text-sm text-gray-300">Requires Downtime</span>
            </label>

            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.auto_approve || false}
                onChange={e => setFormData({ ...formData, auto_approve: e.target.checked })}
                className="w-4 h-4 rounded border-border-dark bg-background-hover text-primary focus:ring-primary"
              />
              <span className="text-sm text-gray-300">Auto-approve (for low-risk standard changes)</span>
            </label>

            {isEdit && (
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.is_active !== undefined ? formData.is_active : true}
                  onChange={e => setFormData({ ...formData, is_active: e.target.checked })}
                  className="w-4 h-4 rounded border-border-dark bg-background-hover text-primary focus:ring-primary"
                />
                <span className="text-sm text-gray-300">Active (template is available for use)</span>
              </label>
            )}
          </div>
        </div>

        {/* Custom Fields Section */}
        <div className="space-y-4 pt-4 border-t border-border-dark">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-white">Custom Fields</h2>
            <button
              onClick={handleAddField}
              className="flex items-center gap-2 px-3 py-1.5 bg-primary/20 text-primary rounded-lg hover:bg-primary/30 transition-colors text-sm"
            >
              <Add size={16} />
              <span>Add Field</span>
            </button>
          </div>
          <p className="text-sm text-gray-400">
            Define team-specific custom fields that will appear when creating a change request from this template.
          </p>

          {customFields.length === 0 && !showFieldForm && (
            <div className="text-center py-8 text-gray-500 border border-dashed border-border-dark rounded-lg">
              <DocumentText size={32} color="#6B7280" className="mx-auto mb-2" />
              <p>No custom fields defined</p>
              <p className="text-xs mt-1">Click "Add Field" to create team-specific fields</p>
            </div>
          )}

          {customFields.length > 0 && (
            <div className="space-y-2">
              {customFields.map((field, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-3 bg-background-hover rounded-lg border border-border-dark"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-white">{field.label}</span>
                      <span className="text-xs px-2 py-0.5 bg-primary/20 text-primary rounded">
                        {field.type}
                      </span>
                      {field.is_required && (
                        <span className="text-xs text-red-400">Required</span>
                      )}
                    </div>
                    {field.description && (
                      <p className="text-xs text-gray-400 mt-1">{field.description}</p>
                    )}
                    <p className="text-xs text-gray-500 mt-1">Key: {field.field_key}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleEditField(index)}
                      className="p-1.5 hover:bg-background-card rounded transition-colors"
                      title="Edit field"
                    >
                      <Edit2 size={16} color="#9CA3AF" />
                    </button>
                    <button
                      onClick={() => handleDeleteField(index)}
                      className="p-1.5 hover:bg-red-500/20 rounded transition-colors"
                      title="Delete field"
                    >
                      <Trash size={16} color="#EF4444" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {showFieldForm && (
            <CustomFieldForm
              initialField={editingFieldIndex !== null ? customFields[editingFieldIndex] : undefined}
              existingFieldKeys={customFields.map(f => f.field_key)}
              onSave={handleSaveField}
              onCancel={handleCancelField}
            />
          )}
        </div>
      </div>
    </div>
  );
};

// Custom Field Form Component
interface CustomFieldFormProps {
  initialField?: TemplateCustomField;
  existingFieldKeys: string[];
  onSave: (field: TemplateCustomField) => void;
  onCancel: () => void;
}

const CustomFieldForm: React.FC<CustomFieldFormProps> = ({
  initialField,
  existingFieldKeys,
  onSave,
  onCancel,
}) => {
  const [fieldData, setFieldData] = useState<TemplateCustomField>({
    field_key: initialField?.field_key || '',
    label: initialField?.label || '',
    type: initialField?.type || 'text',
    is_required: initialField?.is_required || false,
    options: initialField?.options || [],
    placeholder: initialField?.placeholder || '',
    default_value: initialField?.default_value || '',
    description: initialField?.description || '',
  });

  const [newOption, setNewOption] = useState('');

  const fieldTypes: TemplateCustomField['type'][] = ['text', 'number', 'textarea', 'select', 'date', 'boolean'];

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (!fieldData.field_key.trim()) {
      toast.error('Field key is required');
      return;
    }
    if (!fieldData.label.trim()) {
      toast.error('Field label is required');
      return;
    }
    if (fieldData.field_key !== initialField?.field_key && existingFieldKeys.includes(fieldData.field_key)) {
      toast.error('Field key already exists');
      return;
    }
    if (fieldData.type === 'select' && (!fieldData.options || fieldData.options.length === 0)) {
      toast.error('Select fields require at least one option');
      return;
    }

    // Generate field_key from label if not provided
    if (!fieldData.field_key) {
      fieldData.field_key = fieldData.label.toLowerCase().replace(/[^a-z0-9]/g, '_');
    }

    onSave(fieldData);
  };

  const addOption = () => {
    if (newOption.trim() && !fieldData.options?.includes(newOption.trim())) {
      setFieldData({
        ...fieldData,
        options: [...(fieldData.options || []), newOption.trim()],
      });
      setNewOption('');
    }
  };

  const removeOption = (option: string) => {
    setFieldData({
      ...fieldData,
      options: fieldData.options?.filter(o => o !== option) || [],
    });
  };

  return (
    <form onSubmit={handleSubmit} className="p-4 bg-background-hover rounded-lg border border-primary/30 space-y-4">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-md font-semibold text-white">
          {initialField ? 'Edit Custom Field' : 'Add Custom Field'}
        </h3>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm text-gray-400 mb-1">
            Field Label <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={fieldData.label}
            onChange={e => {
              setFieldData({ ...fieldData, label: e.target.value });
              // Auto-generate field_key if not manually set
              if (!initialField || fieldData.field_key === initialField.field_key) {
                const key = e.target.value.toLowerCase().replace(/[^a-z0-9]/g, '_');
                setFieldData(prev => ({ ...prev, field_key: key }));
              }
            }}
            placeholder="e.g., Service Name"
            className="w-full px-3 py-2 bg-background-card rounded-lg border border-border-dark text-white placeholder-gray-500 focus:outline-none focus:border-primary"
            required
          />
        </div>

        <div>
          <label className="block text-sm text-gray-400 mb-1">
            Field Key <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={fieldData.field_key}
            onChange={e => setFieldData({ ...fieldData, field_key: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_') })}
            placeholder="e.g., service_name"
            className="w-full px-3 py-2 bg-background-card rounded-lg border border-border-dark text-white placeholder-gray-500 focus:outline-none focus:border-primary"
            required
          />
          <p className="text-xs text-gray-500 mt-1">Used internally (lowercase, underscores only)</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm text-gray-400 mb-1">
            Field Type <span className="text-red-500">*</span>
          </label>
          <select
            value={fieldData.type}
            onChange={e => {
              const newValue = e.target.value as TemplateCustomField['type'];
              setFieldData(prev => ({ ...prev, type: newValue }));
            }}
            className="w-full px-3 py-2 bg-background-card rounded-lg border border-border-dark text-white focus:outline-none focus:border-primary"
            required
          >
            {fieldTypes.map(type => (
              <option key={type} value={type}>
                {type.charAt(0).toUpperCase() + type.slice(1)}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm text-gray-400 mb-1">Placeholder</label>
          <input
            type="text"
            value={fieldData.placeholder || ''}
            onChange={e => setFieldData({ ...fieldData, placeholder: e.target.value })}
            placeholder="e.g., Enter service name"
            className="w-full px-3 py-2 bg-background-card rounded-lg border border-border-dark text-white placeholder-gray-500 focus:outline-none focus:border-primary"
          />
        </div>
      </div>

      {fieldData.type === 'select' && (
        <div>
          <label className="block text-sm text-gray-400 mb-1">
            Options <span className="text-red-500">*</span>
          </label>
          <div className="flex gap-2 mb-2">
            <input
              type="text"
              value={newOption}
              onChange={e => setNewOption(e.target.value)}
              onKeyPress={e => e.key === 'Enter' && (e.preventDefault(), addOption())}
              placeholder="Add option"
              className="flex-1 px-3 py-2 bg-background-card rounded-lg border border-border-dark text-white placeholder-gray-500 focus:outline-none focus:border-primary"
            />
            <button
              type="button"
              onClick={addOption}
              className="px-3 py-2 bg-primary/20 text-primary rounded-lg hover:bg-primary/30 transition-colors"
            >
              <Add size={16} />
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            {fieldData.options?.map((option, idx) => (
              <span
                key={idx}
                className="inline-flex items-center gap-1 px-2 py-1 bg-primary/20 text-primary rounded text-sm"
              >
                {option}
                <button
                  type="button"
                  onClick={() => removeOption(option)}
                  className="hover:text-red-400"
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        </div>
      )}

      <div>
        <label className="block text-sm text-gray-400 mb-1">Description</label>
        <textarea
          value={fieldData.description || ''}
          onChange={e => setFieldData({ ...fieldData, description: e.target.value })}
          placeholder="Optional description for this field"
          rows={2}
          className="w-full px-3 py-2 bg-background-card rounded-lg border border-border-dark text-white placeholder-gray-500 focus:outline-none focus:border-primary resize-none"
        />
      </div>

      <div className="flex items-center gap-2">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={fieldData.is_required}
            onChange={e => setFieldData({ ...fieldData, is_required: e.target.checked })}
            className="w-4 h-4 rounded border-border-dark bg-background-card text-primary focus:ring-primary"
          />
          <span className="text-sm text-gray-300">Required field</span>
        </label>
      </div>

      <div className="flex justify-end gap-2 pt-2">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 bg-background-card text-gray-300 rounded-lg hover:bg-background-hover transition-colors"
        >
          Cancel
        </button>
        <button
          type="submit"
          className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/80 transition-colors"
        >
          {initialField ? 'Update Field' : 'Add Field'}
        </button>
      </div>
    </form>
  );
};

export default TemplateFormPage;
